
import { platformApiFetch } from '@/services/platformApi/client';
import { toast } from "@/hooks/use-toast";

const requestPasswordResetEmail = async (email: string): Promise<boolean> => {
  try {
    await platformApiFetch('/v1/auth/password-reset-email', {
      method: 'POST',
      email,
      body: JSON.stringify({
        email,
      }),
    });
  } catch (error) {
    console.error("Fallback password reset request failed:", error);
    return false;
  }

  return true;
};

/**
 * Update user password
 * @param newPassword New password
 */
export const updateUserPassword = async (newPassword: string): Promise<{ success: boolean, error?: string }> => {
  try {
    await platformApiFetch<{ success: boolean }>('/v1/auth/password', {
      method: 'POST',
      body: JSON.stringify({ password: newPassword }),
    });

    toast({
      title: "Password updated",
      description: "Your password has been successfully updated.",
    });

    return { success: true };
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : "An unexpected error occurred";
    return { 
      success: false, 
      error: message 
    };
  }
};

/**
 * Reset user password using custom email with Resend
 * @param email User email
 * @param redirectTo URL to redirect to after password reset
 */
export const resetUserPassword = async (email: string, redirectTo: string): Promise<{ success: boolean, error?: string }> => {
  try {
    void redirectTo;
    const succeeded = await requestPasswordResetEmail(email);

    if (!succeeded) {
      return { 
        success: false, 
        error: "Failed to request password reset" 
      };
    }

    toast({
      title: "Password reset email sent",
      description: "Please check your email for a link to reset your password.",
    });
    
    return { success: true };
  } catch (error: unknown) {
    console.error("Exception in resetUserPassword:", error);
    const message = error instanceof Error ? error.message : "An unexpected error occurred";
    return { 
      success: false, 
      error: message 
    };
  }
};

/**
 * Confirm password reset - this will be handled by Supabase automatically
 * when user clicks the link from email
 * @param password New password
 */
export const confirmPasswordReset = async (password: string): Promise<{ success: boolean, error?: string }> => {
  try {
    console.log("Confirming password reset with new password");

    await platformApiFetch<{ success: boolean }>('/v1/auth/password', {
      method: 'POST',
      body: JSON.stringify({
        password,
      }),
    });

    toast({
      title: "Password reset successful",
      description: "Your password has been successfully updated. You can now log in with your new password.",
    });

    return { success: true };
  } catch (error: unknown) {
    console.error("Exception in confirmPasswordReset:", error);
    const message = error instanceof Error ? error.message : "An unexpected error occurred";
    return { 
      success: false, 
      error: message 
    };
  }
};
